import { store } from "@store/index";
import {
  type SideNavigationV2Props,
  type PageV2Component,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { SideNavigationComponentProps } from "@schemas/sideNavigation.schema";
import { HandlePosition, RelationshipCardinality } from "skedyul-db/browser";
import { iconTypes } from "skedyul-ui/ui";
import { close as closeSideNavigation } from "@store/features/layout/sideNavigationSlice";
import {
  createRelationship,
  fetchCRM,
  updateRelationship,
  updateRelationshipLink,
  type GetModelOutput,
  type GetRelationshipOutput,
} from "@store/features/crm/crmSlice";
import { type ChangeEvent } from "react";
import { type SpanCol } from "skedyul-ui/ui";
import { toast } from "sonner";
import { openDeleteRelationshipDialog } from "../../../lib/openMigrationDialog";

const generateOppositeRelationshipCardinality = (
  cardinality: RelationshipCardinality,
): RelationshipCardinality => {
  switch (cardinality) {
    case "ONE_TO_ONE":
      return "ONE_TO_ONE";
    case "ONE_TO_MANY":
      return "MANY_TO_ONE";
  }

  return "ONE_TO_ONE";
};

const getRelationshipCardinalityFromLinks = (
  links: Array<{ cardinality: RelationshipCardinality }> | undefined,
): RelationshipCardinality => {
  if (!links || links.length === 0) {
    return RelationshipCardinality.ONE_TO_ONE;
  }

  const cardinalities = links.map((link) => link.cardinality);

  if (cardinalities.includes(RelationshipCardinality.MANY_TO_MANY)) {
    return RelationshipCardinality.MANY_TO_MANY;
  }

  // If any link is ONE_TO_MANY or MANY_TO_ONE, the relationship is ONE_TO_MANY
  if (
    cardinalities.includes(RelationshipCardinality.ONE_TO_MANY) ||
    cardinalities.includes(RelationshipCardinality.MANY_TO_ONE)
  ) {
    return RelationshipCardinality.ONE_TO_MANY;
  }

  // If all links are ONE_TO_ONE, the relationship is ONE_TO_ONE
  if (cardinalities.every((c) => c === RelationshipCardinality.ONE_TO_ONE)) {
    return RelationshipCardinality.ONE_TO_ONE;
  }

  // Default fallback
  return RelationshipCardinality.ONE_TO_ONE;
};

type EditingRelationship = GetRelationshipOutput & {
  sourceModel?: GetModelOutput;
  targetModel?: GetModelOutput;
  cardinality?: RelationshipCardinality;
  handle?: string;
  reverseAssociationHandle?: string;
  label?: string;
  reverseAssociationLabel?: string;
  sourceHandlePosition?: HandlePosition;
  targetHandlePosition?: HandlePosition;
  onDeleteBehaviorForward?: "NONE" | "CASCADE" | "RESTRICT" | "SET_NULL";
  onDeleteBehaviorReverse?: "NONE" | "CASCADE" | "RESTRICT" | "SET_NULL";
};

export const editRelationshipViewSchema = (
  args: SideNavigationComponentProps<PageV2Component, { subdomain: string }>,
): SideNavigationV2Props<PageV2Component> => {
  const {
    crm: {
      relationship: { data: relationship, isLoading },
      models: { data: models },
    },
    sideNavigation: {
      right: { isOpen },
    },
  } = args.state;

  const isCreating = relationship?.id === "new";

  const cardinalityOptions = [
    { label: "One to One", value: RelationshipCardinality.ONE_TO_ONE },
    { label: "One to Many", value: RelationshipCardinality.ONE_TO_MANY },
    // Existing relationships can be many-to-many, but this form can't create them.
    ...(isCreating
      ? []
      : [
          {
            label: "Many to Many",
            value: RelationshipCardinality.MANY_TO_MANY,
          },
        ]),
  ];

  const editingRelationship = relationship as EditingRelationship;

  // Compute the actual cardinality from links (when editing) or from the stored value (when creating)
  const actualCardinality = isCreating
    ? editingRelationship?.cardinality || RelationshipCardinality.ONE_TO_ONE
    : getRelationshipCardinalityFromLinks(editingRelationship?.links);

  // Find the forward link when editing (for delete behavior)
  const forwardLink =
    !isCreating && editingRelationship?.links
      ? editingRelationship.links.find(
          (link) =>
            link.cardinality === RelationshipCardinality.ONE_TO_ONE ||
            link.cardinality === RelationshipCardinality.ONE_TO_MANY,
        )
      : undefined;

  const deleteBehaviorItems = (card: RelationshipCardinality) => {
    const all = [
      { label: "None", value: "NONE" },
      { label: "Cascade", value: "CASCADE" },
      { label: "Restrict", value: "RESTRICT" },
    ];
    if (card === RelationshipCardinality.ONE_TO_ONE) {
      return [{ label: "Cascade", value: "CASCADE" }];
    }
    return all;
  };

  const getReverseDeleteBehavior = (
    forward: "NONE" | "CASCADE" | "RESTRICT" | "SET_NULL" | undefined,
    forwardCard: RelationshipCardinality,
  ): "NONE" | "CASCADE" | "RESTRICT" => {
    if (forwardCard === RelationshipCardinality.ONE_TO_ONE) {
      if (forward === "SET_NULL") return "NONE";
      return forward || "CASCADE";
    }
    // ONE_TO_MANY forward → MANY_TO_ONE reverse. Never cascade reverse.
    if (forward === "CASCADE") return "NONE";
    // Convert SET_NULL to NONE for reverse behavior
    if (forward === "SET_NULL") return "NONE";
    return forward || "NONE";
  };

  const getDeleteBehaviorHelpText = (
    behavior: string | undefined,
    subjectName: string,
    objectLabel: string,
    isPluralObject: boolean,
  ) => {
    const objectNoun = objectLabel || (isPluralObject ? "records" : "record");
    switch (behavior) {
      case "CASCADE":
        return `When a ${subjectName} is deleted, all associated ${objectNoun} are deleted too.`;
      case "RESTRICT":
        return `You cannot delete a ${subjectName} if it has associated ${objectNoun}.`;
      case "NONE":
      default:
        return `When a ${subjectName} is deleted, ${isPluralObject ? "associated " + objectNoun + " remain" : "the associated " + objectNoun + " remains"}.`;
    }
  };

  return {
    side: "right",
    isOpen,
    width: "700px",
    component: {
      component: "PageV2",
      props: {
        title: isCreating ? "Create Relationship" : "Edit Relationship",
        hideTitle: true,
        header: {
          icon: "Link",
          primaryAction: isCreating
            ? {
                isLoading,
                label: "Create Relationship",
                handleClick: async () => {
                  try {
                    if (
                      !editingRelationship?.sourceModel ||
                      !editingRelationship?.targetModel
                    ) {
                      toast.error("Please select source and target models");
                      return;
                    }

                    // Dispatch action to create relationship
                    await store.dispatch(
                      createRelationship({
                        links: [
                          {
                            sourceField: {
                              handle: editingRelationship.handle || "",
                              label: editingRelationship.label || "",
                            },
                            targetModelId: editingRelationship.targetModel.id,
                            sourceModelId: editingRelationship.sourceModel.id,
                            cardinality: actualCardinality,
                            handlePosition:
                              editingRelationship.sourceHandlePosition ||
                              HandlePosition.LEFT,
                            onDeleteBehavior:
                              actualCardinality ===
                              RelationshipCardinality.ONE_TO_ONE
                                ? "CASCADE"
                                : (editingRelationship.onDeleteBehaviorForward ===
                                  "SET_NULL"
                                    ? "NONE"
                                    : editingRelationship.onDeleteBehaviorForward) ||
                                  "NONE",
                          },
                          {
                            sourceField: {
                              handle:
                                editingRelationship.reverseAssociationHandle ||
                                "",
                              label:
                                editingRelationship.reverseAssociationLabel ||
                                "",
                            },
                            targetModelId: editingRelationship.sourceModel.id,
                            sourceModelId: editingRelationship.targetModel.id,
                            cardinality:
                              generateOppositeRelationshipCardinality(
                                actualCardinality,
                              ),
                            handlePosition:
                              editingRelationship.targetHandlePosition ||
                              HandlePosition.RIGHT,
                            onDeleteBehavior:
                              actualCardinality ===
                              RelationshipCardinality.ONE_TO_ONE
                                ? "CASCADE"
                                : getReverseDeleteBehavior(
                                    editingRelationship.onDeleteBehaviorForward,
                                    actualCardinality,
                                  ),
                          },
                        ],
                      }),
                    );

                    // Refresh CRM data
                    await store.dispatch(fetchCRM());

                    toast.success("Relationship created");

                    store.dispatch(closeSideNavigation({ position: "right" }));
                  } catch (error) {
                    toast.error("Failed to create relationship");
                  }
                },
              }
            : {
                isLoading,
                label: "Update Relationship",
                handleClick: async () => {
                  try {
                    if (!editingRelationship?.id || !forwardLink) {
                      toast.error("Unable to update relationship");
                      return;
                    }

                    const onDeleteBehavior =
                      editingRelationship.onDeleteBehaviorForward ||
                      forwardLink.onDeleteBehavior ||
                      ("NONE" as const);

                    await store.dispatch(
                      updateRelationshipLink({
                        relationshipId: editingRelationship.id,
                        linkId: forwardLink.id,
                        onDeleteBehavior,
                      }),
                    );

                    // Refresh CRM data
                    await store.dispatch(fetchCRM());

                    toast.success("Relationship updated");
                  } catch (error) {
                    toast.error("Failed to update relationship");
                  }
                },
              },
        },
        content: {
          structure: "sidebar",
          components: [
            {
              type: "Card" as const,
              title: "Relationship Details",
              props: {
                header: {
                  title: "Relationship Details",
                  description: isCreating
                    ? "Configure the relationship between models"
                    : "Edit relationship configuration and links",
                },
                actions: [],
                form: {
                  formVersion: "v1",
                  fields: [
                    ...(isCreating
                      ? []
                      : [
                          {
                            component: "Input" as const,
                            span: 12 as SpanCol,
                            mdSpan: 12 as SpanCol,
                            lgSpan: 12 as SpanCol,
                            props: {
                              type: "text" as const,
                              id: "relationshipId",
                              label: "Relationship ID",
                              leftIcon: "Hash" as iconTypes,
                              value:
                                editingRelationship?.id ||
                                "No relationship selected",
                              isDisabled: true,
                              placeholder: "Relationship ID",
                              helpText:
                                "Unique identifier for this relationship",
                            },
                          },
                        ]),
                    {
                      component: "Select" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        id: "relationship-type",
                        label: "Type",
                        value: actualCardinality,
                        placeholder: "Select relationship type",
                        items: cardinalityOptions,
                        helpText:
                          "Defines how records are related between the models",
                        isDisabled: !isCreating,
                        handleChange: (value: string) => {
                          const cardinality = value as RelationshipCardinality;
                          store.dispatch(
                            updateRelationship({
                              cardinality,
                              // Forward association uses plural for ONE_TO_MANY, singular for ONE_TO_ONE
                              handle:
                                cardinality ===
                                RelationshipCardinality.ONE_TO_MANY
                                  ? editingRelationship?.targetModel
                                      ?.handlePlural
                                  : editingRelationship?.targetModel?.handle,
                              label:
                                cardinality ===
                                RelationshipCardinality.ONE_TO_MANY
                                  ? editingRelationship?.targetModel?.namePlural
                                  : editingRelationship?.targetModel?.name,
                              // Reverse association always uses singular since it's either ONE_TO_ONE or MANY_TO_ONE
                              reverseAssociationHandle:
                                editingRelationship?.sourceModel?.handle,
                              reverseAssociationLabel:
                                editingRelationship?.sourceModel?.name,
                            }),
                          );
                        },
                      },
                    },
                  ],
                },
              },
            },
            ...(isCreating
              ? [
                  {
                    type: "Card" as const,
                    title: "Forward Association",
                    props: {
                      header: {
                        title: "Forward Association",
                        description: `A ${editingRelationship?.sourceModel?.name || "Source"} ${actualCardinality === RelationshipCardinality.ONE_TO_ONE ? "has one" : "has many"} ${editingRelationship.label || "targets"}`,
                      },
                      actions: [],
                      form: {
                        formVersion: "v1" as const,
                        fields: [
                          {
                            component: "Input" as const,
                            span: 12 as SpanCol,
                            mdSpan: 6 as SpanCol,
                            lgSpan: 6 as SpanCol,
                            props: {
                              type: "text" as const,
                              id: "label",
                              label: "Label",
                              placeholder: "Relationship label",
                              helpText:
                                "Human-readable label for this relationship",
                              value: editingRelationship?.label || "",
                              handleChange: (
                                e: ChangeEvent<HTMLInputElement>,
                              ) => {
                                store.dispatch(
                                  updateRelationship({
                                    label: e.target.value,
                                  }),
                                );
                              },
                            },
                          },
                          {
                            component: "Input" as const,
                            span: 12 as SpanCol,
                            mdSpan: 6 as SpanCol,
                            lgSpan: 6 as SpanCol,
                            props: {
                              type: "text" as const,
                              id: "handle",
                              label: "Handle",
                              placeholder: "relationship_handle",
                              helpText:
                                "The field handle for the relationship (lowercase, underscores allowed)",
                              value: editingRelationship?.handle || "",
                              handleChange: (
                                e: ChangeEvent<HTMLInputElement>,
                              ) => {
                                store.dispatch(
                                  updateRelationship({
                                    handle: e.target.value,
                                  }),
                                );
                              },
                            },
                          },
                          ...((isCreating
                            ? editingRelationship?.cardinality ||
                              RelationshipCardinality.ONE_TO_ONE
                            : getRelationshipCardinalityFromLinks(
                                editingRelationship?.links,
                              )) === RelationshipCardinality.ONE_TO_MANY
                            ? ([
                                {
                                  component: "Select" as const,
                                  span: 12 as SpanCol,
                                  mdSpan: 12 as SpanCol,
                                  lgSpan: 12 as SpanCol,
                                  props: {
                                    id: "onDeleteBehaviorForward",
                                    label: "On Delete Behavior",
                                    value:
                                      editingRelationship?.onDeleteBehaviorForward ||
                                      "NONE",
                                    items: deleteBehaviorItems(
                                      isCreating
                                        ? editingRelationship?.cardinality ||
                                            RelationshipCardinality.ONE_TO_ONE
                                        : getRelationshipCardinalityFromLinks(
                                            editingRelationship?.links,
                                          ),
                                    ),
                                    placeholder: "Select behavior",
                                    helpText: getDeleteBehaviorHelpText(
                                      editingRelationship?.onDeleteBehaviorForward,
                                      editingRelationship?.sourceModel?.name ||
                                        "source",
                                      editingRelationship?.label ||
                                        editingRelationship?.targetModel
                                          ?.namePlural ||
                                        editingRelationship?.targetModel
                                          ?.name ||
                                        "targets",
                                      (isCreating
                                        ? editingRelationship?.cardinality ||
                                          RelationshipCardinality.ONE_TO_ONE
                                        : getRelationshipCardinalityFromLinks(
                                            editingRelationship?.links,
                                          )) ===
                                        RelationshipCardinality.ONE_TO_MANY,
                                    ),
                                    handleChange: (value: string) => {
                                      store.dispatch(
                                        updateRelationship({
                                          onDeleteBehaviorForward: value as
                                            | "NONE"
                                            | "CASCADE"
                                            | "RESTRICT"
                                            | "SET_NULL",
                                        }),
                                      );
                                    },
                                  },
                                },
                              ] as const)
                            : ([] as const)),
                        ],
                      },
                    },
                  },
                ]
              : [
                  {
                    type: "Card" as const,
                    title: "Relationship Type",
                    props: {
                      header: {
                        title: "Relationship Type",
                        description: "Current relationship configuration",
                      },
                      actions: [],
                      form: {
                        formVersion: "v1" as const,
                        fields: [
                          {
                            component: "Alert" as const,
                            span: 12 as SpanCol,
                            mdSpan: 12 as SpanCol,
                            lgSpan: 12 as SpanCol,
                            props: {
                              variant: "default" as const,
                              title: editingRelationship?.links
                                ? (() => {
                                    // Determine the relationship type based on cardinalities
                                    const cardinalities =
                                      editingRelationship.links.map(
                                        (link) => link.cardinality,
                                      );

                                    if (
                                      cardinalities.includes(
                                        RelationshipCardinality.MANY_TO_MANY,
                                      )
                                    ) {
                                      return "Many-to-Many Relationship";
                                    } else if (
                                      cardinalities.includes(
                                        RelationshipCardinality.ONE_TO_MANY,
                                      ) ||
                                      cardinalities.includes(
                                        RelationshipCardinality.MANY_TO_ONE,
                                      )
                                    ) {
                                      return "One-to-Many Relationship";
                                    } else if (
                                      cardinalities.every(
                                        (c) =>
                                          c ===
                                          RelationshipCardinality.ONE_TO_ONE,
                                      )
                                    ) {
                                      return "One-to-One Relationship";
                                    } else {
                                      return "Complex Relationship";
                                    }
                                  })()
                                : "No Relationship Selected",
                              description: editingRelationship?.links
                                ? `This relationship is established through ${
                                    editingRelationship.links.length
                                  } field${
                                    editingRelationship.links.length > 1
                                      ? "s"
                                      : ""
                                  }: ${editingRelationship.links
                                    .map((link) => {
                                      const sourceModelName =
                                        models?.find(
                                          (m) => m.id === link.sourceModelId,
                                        )?.name || "Unknown Model";
                                      return `${link.sourceField.label} (${link.sourceField.handle}) in ${sourceModelName}`;
                                    })
                                    .join(", ")}.`
                                : "Please select a relationship to view its details.",
                            },
                          },
                          ...(forwardLink &&
                          actualCardinality ===
                            RelationshipCardinality.ONE_TO_MANY
                            ? [
                                {
                                  component: "Select" as const,
                                  span: 12 as SpanCol,
                                  mdSpan: 12 as SpanCol,
                                  lgSpan: 12 as SpanCol,
                                  props: {
                                    id: "onDeleteBehaviorForward",
                                    label: "On Delete Behavior",
                                    value:
                                      editingRelationship?.onDeleteBehaviorForward ||
                                      forwardLink.onDeleteBehavior ||
                                      "NONE",
                                    items:
                                      deleteBehaviorItems(actualCardinality),
                                    placeholder: "Select behavior",
                                    helpText: getDeleteBehaviorHelpText(
                                      forwardLink.onDeleteBehavior,
                                      models?.find(
                                        (m) =>
                                          m.id === forwardLink.sourceModelId,
                                      )?.name || "source",
                                      models?.find(
                                        (m) =>
                                          m.id === forwardLink.targetModelId,
                                      )?.namePlural ||
                                        models?.find(
                                          (m) =>
                                            m.id === forwardLink.targetModelId,
                                        )?.name ||
                                        "targets",
                                      true,
                                    ),
                                    handleChange: (value: string) => {
                                      store.dispatch(
                                        updateRelationship({
                                          onDeleteBehaviorForward: value as
                                            | "NONE"
                                            | "CASCADE"
                                            | "RESTRICT"
                                            | "SET_NULL",
                                        }),
                                      );
                                    },
                                  },
                                },
                              ]
                            : []),
                        ],
                      },
                    },
                  },
                  {
                    type: "Card" as const,
                    title: "Danger Zone",
                    props: {
                      header: {
                        title: "Danger Zone",
                        description: "Actions that can't be undone",
                      },
                      actions: [],
                      form: {
                        formVersion: "v1" as const,
                        fields: [
                          {
                            component: "Alert" as const,
                            span: 12 as SpanCol,
                            mdSpan: 12 as SpanCol,
                            lgSpan: 12 as SpanCol,
                            props: {
                              variant: "destructive" as const,
                              title: "Delete Relationship",
                              description:
                                "This will permanently delete the relationship and all its connections. This action cannot be undone.",
                            },
                          },
                          {
                            component: "Button" as const,
                            span: 12 as SpanCol,
                            mdSpan: 12 as SpanCol,
                            lgSpan: 12 as SpanCol,
                            props: {
                              variant: "destructive" as const,
                              label: "Delete Relationship",
                              handleClick: () => {
                                if (!editingRelationship?.id) {
                                  return;
                                }

                                // Get model names for the confirmation dialog
                                const sourceModelName =
                                  models?.find(
                                    (m) =>
                                      m.id ===
                                      editingRelationship.links?.[0]
                                        ?.sourceModelId,
                                  )?.name || "Unknown Model";

                                const targetModelName =
                                  models?.find(
                                    (m) =>
                                      m.id ===
                                      editingRelationship.links?.[0]
                                        ?.targetModelId,
                                  )?.name || "Unknown Model";

                                void openDeleteRelationshipDialog(
                                  editingRelationship.id,
                                  `${sourceModelName} → ${targetModelName}`,
                                );
                              },
                            },
                          },
                        ],
                      },
                    },
                  },
                  // Linked Apps card - only show when editing and has linked apps
                  ...((
                    editingRelationship as unknown as {
                      linkedApps?: Array<unknown>;
                    }
                  )?.linkedApps?.length
                    ? [
                        {
                          type: "Card" as const,
                          title: "Linked Apps",
                          props: {
                            header: {
                              title: "Linked Apps",
                              description:
                                "Apps that have mapped this relationship to their resources",
                            },
                            form: {
                              formVersion: "v1" as const,
                              fields: [
                                {
                                  component: "DataTable" as const,
                                  span: 12 as SpanCol,
                                  mdSpan: 12 as SpanCol,
                                  lgSpan: 12 as SpanCol,
                                  props: {
                                    data:
                                      (
                                        editingRelationship as unknown as {
                                          linkedApps?: Array<{
                                            id: string;
                                            appName: string;
                                            appHandle: string;
                                            installId: string;
                                            resourceHandle: string;
                                          }>;
                                        }
                                      )?.linkedApps || [],
                                    emptyMessage:
                                      "No apps have linked this relationship to their resources.",
                                    columns: [
                                      {
                                        id: "appName",
                                        type: "rowLayout" as const,
                                        header: "App",
                                        accessorKey: "appName",
                                        align: "left" as const,
                                        components: [
                                          {
                                            component: "Text" as const,
                                            generateProps: (row: {
                                              appName: string;
                                            }) => ({
                                              children: row.appName || "",
                                            }),
                                          },
                                        ],
                                      },
                                      {
                                        id: "resourceHandle",
                                        type: "rowLayout" as const,
                                        header: "Resource",
                                        accessorKey: "resourceHandle",
                                        align: "left" as const,
                                        components: [
                                          {
                                            component: "Text" as const,
                                            generateProps: (row: {
                                              resourceHandle: string;
                                            }) => ({
                                              children:
                                                row.resourceHandle || "",
                                            }),
                                          },
                                        ],
                                      },
                                    ],
                                  },
                                },
                              ],
                            },
                          },
                        },
                      ]
                    : []),
                ]),
          ],
        },
      },
    },
  };
};
